import { useInView } from 'framer-motion';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface IProps {
  children: any;
  className?: any;
}

// The angled highlighter behind a headline phrase, drawn on as the phrase comes
// into view rather than sitting there already finished. Colour still comes from
// the `mark-*` class the caller passes, exactly as it would on a bare span —
// everything this adds is the timing. See `mark-sweep` in utilities.css.
export default function Mark({
  children,
  className = '',
}: IProps): ReactElement {
  const ref = useRef<HTMLSpanElement>(null);

  // `once` so the stroke never redraws on the way back up the page, and 0.8 so
  // it is not left half finished at the bottom edge of the viewport.
  const isInView = useInView(ref, { once: true, amount: 0.8 });

  // The stroke is painted on a pseudo-element covering the phrase's box, which
  // only lines up with the words while the phrase is on one line. Narrow
  // viewports wrap it, and there it keeps the plain static mark.
  const [isUnwrapped, setIsUnwrapped] = useState(false);

  useEffect(() => {
    const mark = ref.current;
    const line = mark?.parentElement;

    if (!mark || !line) return;

    let isMounted = true;

    const measure = () => {
      if (isMounted) setIsUnwrapped(mark.getClientRects().length === 1);
    };

    measure();

    // That first measurement runs on whatever font is loaded at the time, and
    // the fallback metrics can wrap a phrase the real face fits on one line.
    // Worth measuring again on the swap, which does not always change the
    // parent's size and so does not always reach the observer below.
    document.fonts.ready.then(measure);

    // Watching the parent rather than the phrase: a viewport change rewraps it,
    // and ResizeObserver skips inline elements. A caller that nests this inside
    // another inline element gets no callbacks and so keeps the static mark,
    // which is the right way to fail.
    const observer = new ResizeObserver(measure);
    observer.observe(line);

    return () => {
      isMounted = false;
      observer.disconnect();
    };
  }, []);

  return (
    <span
      ref={ref}
      data-sweep={isUnwrapped ? (isInView ? 'on' : 'pending') : 'off'}
      className={twMerge('mark-sweep', className)}>
      {children}
    </span>
  );
}
